/**
 * Fetch content references from brooklyn
 * @module services/content-references
 */
import { stringify as qsStringifyQuery } from 'qs'
import { List, fromJS, Map } from 'immutable'
import _get from 'lodash/get'
import _omitBy from 'lodash/omitBy'
import _isUndefined from 'lodash/isUndefined'
import _split from 'lodash/split'
import _parseInt from 'lodash/parseInt'
import _trim from 'lodash/trim'
import request from '../request'
import {
  CONTENT_TYPE_SERIES,
  CONTENT_TYPE_VIDEO,
  CONTENT_TYPE_PERSON,
  CONTENT_TYPE_TAG,
  CONTENT_TYPE_GUIDE,
  CONTENT_TYPE_GUIDE_DAY,
  CONTENT_TYPE_COURSE,
  CONTENT_TYPE_COURSE_MODULE,
} from '../content/content-types'
import { createAuthorization } from '../auth'
import {
  BUNDLE_ADMIN_CATEGORY,
  BUNDLE_CATEGORY,
  BUNDLE_CENTERS,
  BUNDLE_MARKETING_CENTERS,
  BUNDLE_CONTENT_ORIGIN,
  BUNDLE_GENRES,
  BUNDLE_ONBOARDING_CHOICES,
  BUNDLE_RAINBOW_CATEGORY,
  BUNDLE_SITE_SEGMENT,
} from '../content/bundles'
import {
  CONTENT_FILTER_TYPE_CONSCIOUS_FORMAT,
  CONTENT_FILTER_TYPE_CONSCIOUS_TOPIC,
  CONTENT_FILTER_TYPE_FILM_SUBJECT,
  CONTENT_FILTER_TYPE_FILM_TYPE,
  CONTENT_FILTER_TYPE_FITNESS_DURATION,
  CONTENT_FILTER_TYPE_FITNESS_INSTRUCTOR,
  CONTENT_FILTER_TYPE_FITNESS_LEVEL,
  CONTENT_FILTER_TYPE_FITNESS_SPECIALTY,
  CONTENT_FILTER_TYPE_FITNESS_STYLE,
  CONTENT_FILTER_TYPE_INTERVIEW_COLLECTION,
  CONTENT_FILTER_TYPE_INTERVIEW_GUEST,
  CONTENT_FILTER_TYPE_INTERVIEW_HOST,
  CONTENT_FILTER_TYPE_INTERVIEW_TOPIC,
  CONTENT_FILTER_TYPE_MEDITATION_FOCUS,
  CONTENT_FILTER_TYPE_MEDITATION_STYLE,
  CONTENT_FILTER_TYPE_MEDITATION_TEACHER,
  CONTENT_FILTER_TYPE_MEDITATION_DURATION,
  CONTENT_FILTER_TYPE_RECIPE_DIET,
  CONTENT_FILTER_TYPE_RECIPE_TYPE,
  CONTENT_FILTER_TYPE_THIRD_PARTY_GENRE,
  CONTENT_FILTER_TYPE_YOGA_DURATION,
  CONTENT_FILTER_TYPE_YOGA_FOCUS,
  CONTENT_FILTER_TYPE_YOGA_LEVEL,
  CONTENT_FILTER_TYPE_YOGA_STYLE,
  CONTENT_FILTER_TYPE_YOGA_TEACHER,
  CONTENT_FILTER_TYPE_YI_YOGA_CLASS_STYLE,
  CONTENT_FILTER_TYPE_YI_USER_GOALS,
  CONTENT_FILTER_TYPE_YI_COURSE_CATEGORY,
  CONTENT_FILTER_TYPE_YI_TAGS,
} from '../content-filters/types'
import {
  CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY,
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_ARTICLE_CENTER,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_CONSCIOUS_FORMAT,
  CONTENT_REFERENCE_TYPE_CONSCIOUS_TOPIC,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
  CONTENT_REFERENCE_TYPE_FILM_SUBJECT,
  CONTENT_REFERENCE_TYPE_FILM_TYPE,
  CONTENT_REFERENCE_TYPE_FITNESS_DURATION,
  CONTENT_REFERENCE_TYPE_FITNESS_INSTRUCTOR,
  CONTENT_REFERENCE_TYPE_FITNESS_LEVEL,
  CONTENT_REFERENCE_TYPE_FITNESS_SPECIALTY,
  CONTENT_REFERENCE_TYPE_FITNESS_STYLE,
  CONTENT_REFERENCE_TYPE_GENRES,
  CONTENT_REFERENCE_TYPE_GUIDE,
  CONTENT_REFERENCE_TYPE_GUIDE_DAY_VIDEO,
  CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS,
  CONTENT_REFERENCE_TYPE_MEDITATION_STYLE,
  CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER,
  CONTENT_REFERENCE_TYPE_MEDITATION_DURATION,
  CONTENT_REFERENCE_TYPE_ONBOARDING_CHOICES,
  CONTENT_REFERENCE_TYPE_RECIPE_DIET,
  CONTENT_REFERENCE_TYPE_RECIPE_TYPE,
  CONTENT_REFERENCE_TYPE_SERIES,
  CONTENT_REFERENCE_TYPE_SIMILAR_VIDEOS,
  CONTENT_REFERENCE_TYPE_SITE_SEGMENT,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
  CONTENT_REFERENCE_TYPE_VIDEO_COLLECTION,
  CONTENT_REFERENCE_TYPE_VIDEO_TOPIC,
  CONTENT_REFERENCE_TYPE_YOGA_DURATION,
  CONTENT_REFERENCE_TYPE_YOGA_FOCUS,
  CONTENT_REFERENCE_TYPE_YOGA_LEVEL,
  CONTENT_REFERENCE_TYPE_YOGA_STYLE,
  CONTENT_REFERENCE_TYPE_YOGA_TEACHER,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_PRODUCT,
  CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE,
  CONTENT_REFERENCE_TYPE_COURSE_MODULE_OTHER,
  CONTENT_REFERENCE_TYPE_PRIMARY_COURSE,
  CONTENT_REFERENCE_TYPE_FULL_VIDEO,
  CONTENT_REFERENCE_TYPE_FULL_SERIES,
  CONTENT_REFERENCE_TYPE_YI_YOGA_CLASS_STYLE,
  CONTENT_REFERENCE_TYPE_YI_USER_GOALS,
  CONTENT_REFERENCE_TYPE_YI_COURSE_CATEGORY,
  CONTENT_REFERENCE_TYPE_YI_TAG,
} from './types'

export const REGEX_PARSE_FORM_VALUE = /^([^[]*)(\[(.*):(.*)\])?/

/**
 * Get a list of content references from brooklyn
 * @param {object} options The options
 * @param {string} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {{toContentContentId: Number, toContentContentType: String,
 * fromContentContentId: Number, fromContentContentType: String}[]} options.contentReferences
 * Thecontent reference to look up
 */
export async function getContentReferencesList (options = {}) {
  const { auth, server, contentReferences } = options
  const params = _omitBy({ contentReferences }, _isUndefined)
  const res = await request
    .get(`${server}/v1/content-references/list`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query(qsStringifyQuery(params, { arrayFormat: 'indices', encodeValuesOnly: true }))
  return fromJS(_get(res, 'body', {}))
}

/**
 * Get a list of content reference types from brooklyn
 * @param {object} options The options
 * @param {string} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 */
export async function getContentReferenceTypes (options = {}) {
  const { auth, server } = options
  const res = await request
    .get(`${server}/v1/content-references-types`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
  return fromJS(_get(res, 'body', {}))
}

/**
 * Filter content references by content reference type
 * @param {List} contentReferences The content references with content reference type
 * @param {List} type The content references types to filter by
 */
export function filterByContentReferenceType (contentReferences = List(), types = List()) {
  return contentReferences.filter((v) => types.includes(v.getIn(['contentReferenceType', 'type'])))
}

export function contentReferenceToString (contentReference = Map()) {
  const contentId = contentReference.get('contentId')
  const contentType = contentReference.get('contentType')
  const type = contentReference.get('type')
  return `${contentId}:${contentType}:${type}`
}

export function stringToContentReference (string) {
  const [contentId, contentType, type] = _split(string, ':')
  return Map({
    contentId: _parseInt(contentId),
    contentType,
    type,
  })
}

export function parseFieldValue (value) {
  const matches = REGEX_PARSE_FORM_VALUE.exec(value)
  return {
    contentId: matches[3] ? _parseInt(matches[3]) : undefined,
    contentType: matches[4] ? matches[4] : undefined,
    title: matches[1] ? _trim(matches[1]) : undefined,
  }
}

export function contentReferenceToFieldValue (ref = Map()) {
  return `${ref.get('title')} [${ref.get('contentId')}:${ref.get('contentType')}]`
}

/**
 * Get a dictionary which maps content reference types to options
 * which are used to look up content option details
 */
export function getContentReferenceTypesContentDictionary () {
  return Map({
    [CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY]: Map({
      label: 'Admin category',
      contentType: List([CONTENT_TYPE_TAG]),
      bundle: BUNDLE_ADMIN_CATEGORY,
    }),
    [CONTENT_REFERENCE_TYPE_CATEGORY]: Map({
      label: 'Categories',
      contentType: List([CONTENT_TYPE_TAG]),
      bundle: BUNDLE_CATEGORY,
    }),
    [CONTENT_REFERENCE_TYPE_CENTERS]: Map({
      label: 'Centers',
      contentType: List([CONTENT_TYPE_TAG]),
      bundle: BUNDLE_CENTERS,
    }),
    [CONTENT_REFERENCE_TYPE_ARTICLE_CENTER]: Map({
      label: 'Article centers',
      contentType: List([CONTENT_TYPE_TAG]),
      bundle: BUNDLE_CENTERS,
    }),
    [CONTENT_REFERENCE_TYPE_MARKETING_CENTERS]: Map({
      label: 'Marketing Centers',
      contentType: List([CONTENT_TYPE_TAG]),
      bundle: BUNDLE_MARKETING_CENTERS,
    }),
    [CONTENT_REFERENCE_TYPE_CONSCIOUS_FORMAT]: Map({
      label: 'Conscious format',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_CONSCIOUS_FORMAT,
    }),
    [CONTENT_REFERENCE_TYPE_CONSCIOUS_TOPIC]: Map({
      label: 'Conscious topic',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_CONSCIOUS_TOPIC,
    }),
    [CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY]: Map({
      label: 'Rainbow Category',
      contentType: List([CONTENT_TYPE_TAG]),
      bundle: BUNDLE_RAINBOW_CATEGORY,
    }),
    [CONTENT_REFERENCE_TYPE_PRODUCT]: Map({
      label: 'Product',
      contentType: List([CONTENT_REFERENCE_TYPE_PRODUCT]),
    }),
    [CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN]: Map({
      label: 'Content origin',
      contentType: List([CONTENT_TYPE_TAG]),
      bundle: BUNDLE_CONTENT_ORIGIN,
    }),
    [CONTENT_REFERENCE_TYPE_FILM_SUBJECT]: Map({
      label: 'Film subject',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_FILM_SUBJECT,
    }),
    [CONTENT_REFERENCE_TYPE_FILM_TYPE]: Map({
      label: 'Film type',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_FILM_TYPE,
    }),
    [CONTENT_REFERENCE_TYPE_FITNESS_DURATION]: Map({
      label: 'Fitness duration',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_FITNESS_DURATION,
    }),
    [CONTENT_REFERENCE_TYPE_FITNESS_INSTRUCTOR]: Map({
      label: 'Fitness instructor',
      contentType: List([CONTENT_TYPE_PERSON]),
      filterType: CONTENT_FILTER_TYPE_FITNESS_INSTRUCTOR,
    }),
    [CONTENT_REFERENCE_TYPE_FITNESS_LEVEL]: Map({
      label: 'Fitness level',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_FITNESS_LEVEL,
    }),
    [CONTENT_REFERENCE_TYPE_FITNESS_SPECIALTY]: Map({
      label: 'Fitness specialty',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_FITNESS_SPECIALTY,
    }),
    [CONTENT_REFERENCE_TYPE_FITNESS_STYLE]: Map({
      label: 'Fitness style',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_FITNESS_STYLE,
    }),
    [CONTENT_REFERENCE_TYPE_GENRES]: Map({
      label: 'Genres',
      contentType: List([CONTENT_TYPE_TAG]),
      bundle: BUNDLE_GENRES,
    }),
    [CONTENT_REFERENCE_TYPE_GUIDE]: Map({
      label: 'Guide',
      contentType: List([CONTENT_TYPE_GUIDE]),
    }),
    [CONTENT_REFERENCE_TYPE_GUIDE_DAY_VIDEO]: Map({
      label: 'Guide day',
      contentType: List([CONTENT_TYPE_GUIDE_DAY]),
    }),
    [CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST]: Map({
      label: 'Interview guest',
      contentType: List([CONTENT_TYPE_PERSON]),
      filterType: CONTENT_FILTER_TYPE_INTERVIEW_GUEST,
    }),
    [CONTENT_REFERENCE_TYPE_INTERVIEW_HOST]: Map({
      label: 'Interview host',
      contentType: List([CONTENT_TYPE_PERSON]),
      filterType: CONTENT_FILTER_TYPE_INTERVIEW_HOST,
    }),
    [CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS]: Map({
      label: 'Meditation focus',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_MEDITATION_FOCUS,
    }),
    [CONTENT_REFERENCE_TYPE_MEDITATION_STYLE]: Map({
      label: 'Meditation style',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_MEDITATION_STYLE,
    }),
    [CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER]: Map({
      label: 'Meditation teacher',
      contentType: List([CONTENT_TYPE_PERSON]),
      filterType: CONTENT_FILTER_TYPE_MEDITATION_TEACHER,
    }),
    [CONTENT_REFERENCE_TYPE_MEDITATION_DURATION]: Map({
      label: 'Meditation duration',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_MEDITATION_DURATION,
    }),
    [CONTENT_REFERENCE_TYPE_ONBOARDING_CHOICES]: Map({
      label: 'Onboarding choices',
      contentType: List([CONTENT_TYPE_TAG]),
      bundle: BUNDLE_ONBOARDING_CHOICES,
    }),
    [CONTENT_REFERENCE_TYPE_RECIPE_DIET]: Map({
      label: 'Recipe diet',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_RECIPE_DIET,
    }),
    [CONTENT_REFERENCE_TYPE_RECIPE_TYPE]: Map({
      label: 'Recipe type',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_RECIPE_TYPE,
    }),
    [CONTENT_REFERENCE_TYPE_SERIES]: Map({
      label: 'Series',
      contentType: List([CONTENT_TYPE_SERIES]),
    }),
    [CONTENT_REFERENCE_TYPE_SIMILAR_VIDEOS]: Map({
      label: 'Similar videos',
      contentType: List([CONTENT_TYPE_VIDEO]),
    }),
    [CONTENT_REFERENCE_TYPE_FULL_VIDEO]: Map({
      label: 'Full video',
      contentType: List([CONTENT_TYPE_VIDEO]),
    }),
    [CONTENT_REFERENCE_TYPE_FULL_SERIES]: Map({
      label: 'Full series',
      contentType: List([CONTENT_TYPE_SERIES]),
    }),
    [CONTENT_REFERENCE_TYPE_SITE_SEGMENT]: Map({
      label: 'Site segment',
      contentType: List([CONTENT_TYPE_TAG]),
      bundle: BUNDLE_SITE_SEGMENT,
    }),
    [CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE]: Map({
      label: 'Third party genre',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_THIRD_PARTY_GENRE,
    }),
    [CONTENT_REFERENCE_TYPE_VIDEO_COLLECTION]: Map({
      label: 'Video collection',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_INTERVIEW_COLLECTION,
    }),
    [CONTENT_REFERENCE_TYPE_VIDEO_TOPIC]: Map({
      label: 'Video topic',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_INTERVIEW_TOPIC,
    }),
    [CONTENT_REFERENCE_TYPE_YOGA_DURATION]: Map({
      label: 'Yoga duration',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_YOGA_DURATION,
    }),
    [CONTENT_REFERENCE_TYPE_YOGA_FOCUS]: Map({
      label: 'Yoga focus',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_YOGA_FOCUS,
    }),
    [CONTENT_REFERENCE_TYPE_YOGA_LEVEL]: Map({
      label: 'Yoga level',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_YOGA_LEVEL,
    }),
    [CONTENT_REFERENCE_TYPE_YOGA_STYLE]: Map({
      label: 'Yoga style',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_YOGA_STYLE,
    }),
    [CONTENT_REFERENCE_TYPE_YOGA_TEACHER]: Map({
      label: 'Yoga teacher',
      contentType: List([CONTENT_TYPE_PERSON]),
      filterType: CONTENT_FILTER_TYPE_YOGA_TEACHER,
    }),
    [CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE]: Map({
      label: 'Course Module Practice',
      contentType: List([CONTENT_TYPE_COURSE_MODULE]),
    }),
    [CONTENT_REFERENCE_TYPE_COURSE_MODULE_OTHER]: Map({
      label: 'Course Module Other',
      contentType: List([CONTENT_TYPE_COURSE_MODULE]),
    }),
    [CONTENT_REFERENCE_TYPE_PRIMARY_COURSE]: Map({
      label: 'Primary Course',
      contentType: List([CONTENT_TYPE_COURSE]),
    }),

    [CONTENT_REFERENCE_TYPE_YI_YOGA_CLASS_STYLE]: Map({
      label: 'Yoga Class Styles',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_YI_YOGA_CLASS_STYLE,
    }),
    [CONTENT_REFERENCE_TYPE_YI_USER_GOALS]: Map({
      label: 'User Goals',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_YI_USER_GOALS,
    }),
    [CONTENT_REFERENCE_TYPE_YI_COURSE_CATEGORY]: Map({
      label: 'Course Category',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_YI_COURSE_CATEGORY,
    }),
    [CONTENT_REFERENCE_TYPE_YI_TAG]: Map({
      label: 'Tag',
      contentType: List([CONTENT_TYPE_TAG]),
      filterType: CONTENT_FILTER_TYPE_YI_TAGS,
    }),
  })
}
